import axios from "axios";
import {isApiSessionBound, sessionChangedError, apiSessionScope, invalidateApiAccess} from './sessionScope';
import {captureReliableMutation,prepareReliableMutation,acceptOperationResponse,rejectOperationResponse,markOperationUnknown} from './reliableOperations';

// Empty means same origin: the web server forwards /api to the backend.
export const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
export const API = `${BACKEND}/api`;
export const TOKEN_KEY = "autotu_token";

export const api = axios.create({ baseURL: API });
const isLoginRequest = config => /(?:^|\/)auth\/login\/?(?:[?#]|$)/.test(config?.url || "");
const isSessionRequest = config => /(?:^|\/)(?:auth\/[^/?#]+|local-demo\/accounts)\/?(?:[?#]|$)/.test(config?.url || '');
const authorizationFor = config => typeof config?.headers?.get === 'function'
  ? config.headers.get('Authorization') : config?.headers?.Authorization ?? config?.headers?.authorization;

api.interceptors.request.use((cfg) => {
  try{cfg.reliableSession?.assertCurrent();}catch(error){markOperationUnknown(cfg);throw error;}
  const t = localStorage.getItem(TOKEN_KEY);
  if (!isSessionRequest(cfg) && !isApiSessionBound(t)) {markOperationUnknown(cfg);throw sessionChangedError();}
  cfg.headers ??= {};
  const scope = apiSessionScope();
  if (!isSessionRequest(cfg)) cfg.accessEpoch = scope.epoch;
  if (scope.user && !isLoginRequest(cfg) && !cfg.headers["X-Active-Site"]) {
    cfg.headers["X-Active-Site"] = scope.user.active_site_id;
  }
  const hasAuthorization = typeof cfg.headers.has === "function"
    ? cfg.headers.has("Authorization")
    : Object.keys(cfg.headers).some(name => name.toLowerCase() === "authorization");
  if (t && !isLoginRequest(cfg) && !hasAuthorization) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
}, error => {throw error;}, {synchronous: true});

const changedAccess = response => {
  const cfg = response?.config;
  if (!cfg || isSessionRequest(cfg)) return false;
  const scope = apiSessionScope();
  if (cfg.accessEpoch != null && cfg.accessEpoch !== scope.epoch) return true;
  // A confirmed handover can move the driver out of this assignment. Only its
  // minimal acknowledgement may finish before the wizard clears its draft.
  const confirmation = cfg.handoverConfirmation;
  const completed = response.headers?.get?.('X-Handover-Completed') ?? response.headers?.['x-handover-completed'];
  const path = String(cfg.url || '').split('?')[0];
  const ack = response.data;
  const handoverAck = confirmation &&
    cfg.accessEpoch === scope.epoch && authorizationFor(cfg) === `Bearer ${localStorage.getItem(TOKEN_KEY)}` &&
    String(cfg.method).toLowerCase() === 'put' && response.status >= 200 && response.status < 300 &&
    String(completed) === '1' && ack?.id === confirmation.assignmentId &&
    ['delivery','return'].includes(confirmation.operation) && ack.operation_confirmed === confirmation.operation &&
    ack.status === (confirmation.operation === 'delivery' ? 'in_corso' : 'conclusa') &&
    typeof ack.access_refresh === 'boolean' &&
    Object.keys(ack).every(key => ['id','status','operation_confirmed','access_refresh'].includes(key)) &&
    [ `/assignments/${encodeURIComponent(confirmation.assignmentId)}`, `/assignments/${encodeURIComponent(confirmation.assignmentId)}/return-confirmation` ].includes(path);
  if (handoverAck) return false;
  const revoked = response.headers?.get?.('X-Access-Changed') ?? response.headers?.['x-access-changed'];
  if (String(revoked) === '1' || response.status === 409 && response.data?.detail === 'ACCESS_CHANGED') {
    invalidateApiAccess();
    return true;
  }
  const revision = response.headers?.get?.('X-Access-Revision') ?? response.headers?.['x-access-revision'];
  if (scope.user && revision != null && String(revision) !== String(scope.user.access_revision)) {
    invalidateApiAccess();
    return true;
  }
  const version = response.headers?.get?.('X-Access-Version') ?? response.headers?.['x-access-version'];
  if (scope.user?.access_version != null && version != null && String(version) !== String(scope.user.access_version)) {
    invalidateApiAccess();
    return true;
  }
  return false;
};
api.interceptors.response.use(
  (r) => {
    const authorization = authorizationFor(r.config);
    if (!isLoginRequest(r.config) && authorization
        && authorization !== `Bearer ${localStorage.getItem(TOKEN_KEY)}`) {
      markOperationUnknown(r.config);
      return Promise.reject(sessionChangedError());
    }
    if (changedAccess(r)) {markOperationUnknown(r.config);return Promise.reject(sessionChangedError());}
    return r;
  },
  (e) => {
    const authorization = authorizationFor(e.config);
    const currentToken = localStorage.getItem(TOKEN_KEY);
    if (!isLoginRequest(e.config) && authorization && authorization !== `Bearer ${currentToken}`) {
      markOperationUnknown(e.config);
      return Promise.reject(sessionChangedError());
    }
    if (changedAccess({...e.response, config:e.config})) {markOperationUnknown(e.config);return Promise.reject(sessionChangedError());}
    // A late failure from an older request must not revoke a newer login,
    // including one completed in another browser tab.
    if (e.response?.status === 401 && !isLoginRequest(e.config) && currentToken
        && authorization === `Bearer ${currentToken}`) {
      localStorage.removeItem(TOKEN_KEY);
      if (!window.location.pathname.includes("/login")) {
        window.location.href = "/login";
      }
    }
    return Promise.reject(e);
  }
);

// Axios runs request interceptors in reverse registration order. Reliable
// commands are frozen/persisted first, then the existing session guard runs.
api.interceptors.request.use(config=>prepareReliableMutation(config,API),undefined,{runWhen:captureReliableMutation});
// The session guard above must accept a response before any business receipt
// can be delivered. Changed-context responses remain unknown in the registry.
api.interceptors.response.use(acceptOperationResponse,rejectOperationResponse);

export function apiError(e) {
  if(e?.operationState || e?.code==='ERR_SESSION_CHANGED')return e.message;
  const d = e?.response?.data?.detail;
  if (d == null) return "Si è verificato un errore. Riprova.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join(" ");
  if (typeof d.message === 'string') return d.message;
  if (d.code === 'OPERATION_KEY_CONFLICT') return 'Questo riferimento appartiene a una richiesta con dati diversi. Verifica la richiesta precedente prima di prepararne una nuova.';
  return String(d);
}
